import { apiService, ApiResponse } from './api';
import { 
  SalesMetrics, 
  FleetUtilization, 
  SalesAnalyticsFilters, 
  SalesDashboardData,
  VehiclePerformanceData,
  RevenueData,
  PeriodComparison
} from '../types/analytics';

class AnalyticsService {
  // Sales Analytics Methods
  async getSalesMetrics(filters: SalesAnalyticsFilters): Promise<ApiResponse<SalesMetrics[]>> {
    console.log('📊 AnalyticsService: getSalesMetrics called with filters:', filters);
    const params = new URLSearchParams({
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
      ...(filters.period && { period: filters.period }),
      ...(filters.vehicleType && { vehicleType: filters.vehicleType }),
      ...(filters.location && { location: filters.location })
    });

    const endpoint = `/api/analytics/sales-metrics?${params}`;
    console.log('🔗 AnalyticsService: Calling endpoint:', endpoint);
    
    try {
      const response = await apiService.get<SalesMetrics[]>(endpoint);
      console.log('✅ AnalyticsService: getSalesMetrics response:', response);
      return response;
    } catch (error) {
      console.error('❌ AnalyticsService: getSalesMetrics error:', error);
      throw error;
    }
  }

  async getRevenueByPeriod(startDate: Date, endDate: Date): Promise<ApiResponse<RevenueData[]>> {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    return apiService.get<RevenueData[]>(`/api/analytics/revenue-by-period?${params}`);
  }

  async getSalesDashboardData(filters: SalesAnalyticsFilters): Promise<ApiResponse<SalesDashboardData>> {
    const params = new URLSearchParams({
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
      ...(filters.period && { period: filters.period })
    });

    return apiService.get<SalesDashboardData>(`/api/analytics/sales-dashboard?${params}`);
  }

  async getPeriodComparison(
    currentStart: Date, 
    currentEnd: Date, 
    previousStart: Date, 
    previousEnd: Date
  ): Promise<ApiResponse<PeriodComparison>> {
    const params = new URLSearchParams({
      currentStart: currentStart.toISOString(),
      currentEnd: currentEnd.toISOString(),
      previousStart: previousStart.toISOString(),
      previousEnd: previousEnd.toISOString()
    });

    return apiService.get<PeriodComparison>(`/api/analytics/period-comparison?${params}`);
  }

  // Vehicle Performance Methods
  async getVehiclePerformance(): Promise<ApiResponse<VehiclePerformanceData[]>> {
    return apiService.get<VehiclePerformanceData[]>('/api/analytics/vehicle-performance');
  }

  async getTopPerformingVehicles(limit: number = 10): Promise<ApiResponse<VehiclePerformanceData[]>> {
    return apiService.get<VehiclePerformanceData[]>(`/api/analytics/top-vehicles?limit=${limit}`);
  }

  async getFleetUtilization(startDate: Date, endDate: Date): Promise<ApiResponse<FleetUtilization[]>> {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    return apiService.get<FleetUtilization[]>(`/api/analytics/fleet-utilization?${params}`);
  }

  // Real-time Analytics Methods
  async getDashboardSummary(startDate: Date, endDate: Date): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    return apiService.get(`/api/analytics/dashboard-summary?${params}`);
  }

  async getBookingTrends(startDate: Date, endDate: Date): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    return apiService.get<any[]>(`/api/analytics/booking-trends?${params}`);
  }

  // Utility Methods
  calculatePeriodDates(period: 'daily' | 'weekly' | 'monthly' | 'yearly', date: Date = new Date()): {
    current: { start: Date; end: Date };
    previous: { start: Date; end: Date };
  } {
    const current = { start: new Date(date), end: new Date(date) };
    const previous = { start: new Date(date), end: new Date(date) };

    switch (period) {
      case 'daily':
        current.start.setHours(0, 0, 0, 0);
        current.end.setHours(23, 59, 59, 999);
        previous.start.setDate(previous.start.getDate() - 1);
        previous.end.setDate(previous.end.getDate() - 1);
        previous.start.setHours(0, 0, 0, 0);
        previous.end.setHours(23, 59, 59, 999);
        break;

      case 'weekly':
        const dayOfWeek = current.start.getDay();
        current.start.setDate(current.start.getDate() - dayOfWeek);
        current.start.setHours(0, 0, 0, 0);
        current.end.setDate(current.start.getDate() + 6);
        current.end.setHours(23, 59, 59, 999);
        previous.start.setDate(current.start.getDate() - 7);
        previous.end.setDate(current.end.getDate() - 7);
        break;

      case 'monthly':
        current.start.setDate(1);
        current.start.setHours(0, 0, 0, 0);
        current.end.setMonth(current.end.getMonth() + 1, 0);
        current.end.setHours(23, 59, 59, 999);
        previous.start.setMonth(current.start.getMonth() - 1, 1);
        previous.end.setMonth(current.start.getMonth(), 0);
        previous.start.setHours(0, 0, 0, 0);
        previous.end.setHours(23, 59, 59, 999);
        break;

      case 'yearly':
        current.start.setMonth(0, 1);
        current.start.setHours(0, 0, 0, 0);
        current.end.setMonth(11, 31);
        current.end.setHours(23, 59, 59, 999);
        previous.start.setFullYear(current.start.getFullYear() - 1, 0, 1);
        previous.end.setFullYear(current.end.getFullYear() - 1, 11, 31);
        previous.start.setHours(0, 0, 0, 0);
        previous.end.setHours(23, 59, 59, 999);
        break;
    }

    return { current, previous };
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  formatPercentage(value: number): string {
    return `${(value * 100).toFixed(1)}%`;
  }

  calculatePercentageChange(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }
}

export const analyticsService = new AnalyticsService();